/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { History } from "history"
import { Dispatch } from "redux"
import { MENUS } from "constants/routes"
import { waitMenuGroupPropagete } from "utils/menu_group"
import {
  FetchMenuGroupDetailRtkResult,
  fetchMenuGroupDetailWithMigration,
} from "utils/rtk_migration_helper"
import { FetchMenuGroupDetailRequest } from "queries/types"

/**
 * Refresh flow shared by the link/unlink store drawers after a successful
 * mutation: wait for the menu group to propagate, then navigate to the menu
 * group detail when the mutation moved it to a different ID, or run the
 * caller's same-menu-group refresh otherwise. Returns false when the refresh
 * failed.
 */
export const refreshMenuGroupAfterStoreChange = async ({
  updatedMenuGroupID,
  menuGroupID,
  merchantGroupID,
  dispatch,
  isRtkFetchMenuGroupDetailEnabled,
  fetchMenuGroupDetailRtk,
  history,
  onSameMenuGroup,
}: {
  updatedMenuGroupID: string
  menuGroupID: string
  merchantGroupID: string
  dispatch: Dispatch
  isRtkFetchMenuGroupDetailEnabled: boolean
  fetchMenuGroupDetailRtk: (
    request: FetchMenuGroupDetailRequest,
  ) => PromiseLike<FetchMenuGroupDetailRtkResult>
  history: History
  onSameMenuGroup: () => void | Promise<void>
}): Promise<boolean> => {
  const didPropagate = await waitMenuGroupPropagete({
    menuGroupID,
    merchantGroupID,
    dispatch,
    isRtkEnabled: isRtkFetchMenuGroupDetailEnabled,
    triggerFetchMenuGroupDetailRtk: fetchMenuGroupDetailRtk,
  })
  if (!didPropagate) return false

  if (updatedMenuGroupID !== menuGroupID) {
    const fetchResult = await fetchMenuGroupDetailWithMigration({
      dispatch,
      fetchMenuGroupDetailRtk,
      isRtkFetchMenuGroupDetailEnabled,
      request: {
        menuGroupID: updatedMenuGroupID,
        merchantGroupID,
      },
    })
    if (!fetchResult?.type || fetchResult.type.endsWith("_FAIL")) return false
    history.replace(`${MENUS}/${updatedMenuGroupID}`)
  } else {
    await onSameMenuGroup()
  }
  return true
}
